
<template>
  <div id="tour-course-details-page" class="space-y-6 m-6 mb-10">

    <!-- Header -->
    <div class="flex items-center gap-2">
      <Button size="small" icon="pi pi-arrow-left" text @click="goBack" />
      <h1 class="text-2xl font-semibold">Course Details</h1>
    </div>

    <!-- Loading Course -->
    <div v-if="loadingCourse" class="flex justify-center py-12">
      <i class="pi pi-spin pi-spinner text-3xl text-primary"></i>
    </div>

    <!-- Course Not Found -->
    <div v-else-if="!course" class="bg-yellow-50 border p-4 rounded">
      <p class="text-yellow-800">Course not found.</p>
    </div>

    <!-- Course Info -->
    <template v-else>
      <div class="bg-white rounded shadow p-6 space-y-4">
        <div class="flex items-center gap-4">
          <div>
            <p class="text-sm text-gray-500">Course Code</p>
            <p class="text-xl font-semibold">{{ course.course_code }}</p>
          </div>
          <Tag v-if="course.is_active" value="Active" severity="success" />
        </div>

        <div>
          <p class="text-sm text-gray-500">Course Title</p>
          <p class="text-lg font-semibold">{{ course.course_title }}</p>
        </div>

        <div>
          <p class="text-sm text-gray-500">Units</p>
          <p class="text-lg font-semibold">{{ course.unit }}</p>
        </div>
      </div>

      <!-- Lecturers -->
      <div v-if="isTertiary" class="bg-white rounded shadow p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-semibold">Lecturers in this Semester</h3>
          <Button
            v-if="canAllocateStudents"
            icon="pi pi-users"
            label="Allocate Students"
            size="small"
            severity="help"
            class="p-button-outlined"
            @click="router.push({ name: 'admin-student-allocation', params: { courseId } })"
          />
        </div>

        <AcademicCalendarSelect
          v-model="selectedAcademicCalendar"
          class="max-w-sm mb-4"
        />

        <!-- Loading -->
        <div v-if="loadingLecturers" class="flex justify-center py-8">
          <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
        </div>

        <!-- Table -->
        <DataTable
          size="small"
          v-else-if="lecturers.length"
          :value="lecturers"
          paginator
          :rows="10"
        >
          <Column header="Lecturer">
            <template #body="{ data }">
              {{ data.user?.name || '—' }}
            </template>
          </Column>

          <Column header="Status">
            <template #body="{ data }">
              <Tag
                v-if="data.is_approved"
                value="Approved"
                severity="success"
              />
              <Tag
                v-else-if="data.denied_at"
                value="Denied"
                severity="danger"
              />
              <Tag
                v-else
                value="Pending"
                severity="warning"
              />
            </template>
          </Column>
        </DataTable>

        <!-- Empty -->
        <p
          v-else
          class="text-center py-12 text-gray-500"
        >
          No lecturers assigned for this academic calendar.
        </p>
      </div>

      <!-- Registered Students -->
      <div v-if="isTertiary" class="bg-white rounded shadow p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-semibold">
            Registered Students
            <span v-if="registrations.length" class="text-base font-normal text-gray-500">
              ({{ registrations.length }})
            </span>
          </h3>
        </div>

        <!-- Loading -->
        <div v-if="loadingRegistrations" class="flex justify-center py-8">
          <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
        </div>

        <!-- Table -->
        <DataTable
          size="small"
          v-else-if="registrations.length"
          :value="registrations"
          paginator
          :rows="20"
          :rowsPerPageOptions="[10, 20, 50, 100]"
          filterDisplay="row"
          v-model:filters="studentFilters"
          sortField="student.surname"
          :sortOrder="1"
        >
          <Column header="S/N" style="width: 50px">
            <template #body="{ index }">
              {{ index + 1 }}
            </template>
          </Column>

          <Column header="Matric No" filterField="student.matric_no" :showFilterMenu="false" sortable sortField="student.matric_no">
            <template #body="{ data }">
              {{ data.student?.matric_no || '—' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText v-model="filterModel.value" @input="filterCallback()" placeholder="Search..." size="small" class="w-full" />
            </template>
          </Column>

          <Column header="Name" filterField="studentName" :showFilterMenu="false" sortable sortField="student.surname">
            <template #body="{ data }">
              {{ data.student?.surname }} {{ data.student?.first_name }} {{ data.student?.other_name || '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText v-model="filterModel.value" @input="filterCallback()" placeholder="Search..." size="small" class="w-full" />
            </template>
          </Column>

          <Column header="Programme" sortable sortField="programme.program_name">
            <template #body="{ data }">
              {{ data.programme?.program_name || data.programme?.prog_code || '—' }}
            </template>
          </Column>

          <Column header="Level" sortable sortField="registration_level">
            <template #body="{ data }">
              {{ data.registration_level || '—' }}
            </template>
          </Column>

          <Column header="Status">
            <template #body="{ data }">
              <Tag
                :value="registrationApprovalLabel(data.approval_status, 'Pending')"
                :severity="getStatusSeverity(data.approval_status || 'pending')"
                data-testid="registration-status"
              />
            </template>
          </Column>
        </DataTable>

        <!-- Empty -->
        <p
          v-else
          class="text-center py-12 text-gray-500"
        >
          No students registered for this course in the selected semester.
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import { getStatusSeverity } from '@/utils/statusSeverity';
import { courseSemesterVariables } from '@/utils/courseSemesterVariables';
import { registrationApprovalLabel } from '@/utils/registrationApprovalStatus';

import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import {
  useGetCourseByIdQuery,
  useGetCourseLecturersInSemesterQuery,
  useGetRegistrationsForCourseInCurrentCalendarQuery,
} from '@/types/vue-apollo-helpers';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
// The route behind this button is gated on allocate_students; hiding it here
// keeps plain lecturers from a dead-end redirect.
const canAllocateStudents = computed(() =>
  can(userStore.currentUser, AppPermissions.LecturerCourseAllocation.Actions.AllocateStudents, AppPermissions.LecturerCourseAllocation.Resource));
const { isTertiary } = useSchoolFeatures();

// The admin shell remounts this page for another course, so reading the id once is safe.
const courseId = Number(route?.params?.id);
// AcademicCalendarSelect hands back the calendar's ID, which is a string.
const selectedAcademicCalendar = ref<string | number | null>(null);

const course = ref<Record<string, unknown> | null>(null);
const lecturers = ref<Record<string, unknown>[]>([]);
const registrations = ref<Record<string, unknown>[]>([]);

const studentFilters = ref({
  'student.matric_no': { value: null, matchMode: 'contains' },
  'studentName': { value: null, matchMode: 'contains' },
});

/* ---------------- COURSE ---------------- */
const {
  result: courseResult,
  loading: loadingCourse,
} = useGetCourseByIdQuery({ id: courseId });

watchEffect(() => {
  if (courseResult.value?.getCourse) {
    course.value = courseResult.value.getCourse;
  }
});

// Both lists need a real course id and the semester picked above them (the
// picker selects the active one by default). Before that, nothing is sent:
// the old `skip` option is not an @vue/apollo-composable option, so the
// queries went out with a null course and a string calendar id.
const semesterVariables = computed(() => courseSemesterVariables(courseId, selectedAcademicCalendar.value));

/* ---------------- LECTURERS ---------------- */
const {
  result: lecturersResult,
  loading: loadingLecturers,
} = useGetCourseLecturersInSemesterQuery(
  () => semesterVariables.value ?? { course_id: 0 },
  () => ({
    enabled: isTertiary.value && semesterVariables.value !== null,
    fetchPolicy: 'network-only',
  }),
);

watchEffect(() => {
  lecturers.value =
    lecturersResult.value?.getCourseLecturersInSemester ?? [];
});

/* ---------------- REGISTERED STUDENTS ---------------- */
const {
  result: registrationsResult,
  loading: loadingRegistrations,
} = useGetRegistrationsForCourseInCurrentCalendarQuery(
  () => semesterVariables.value ?? { course_id: 0 },
  () => ({
    enabled: isTertiary.value && semesterVariables.value !== null,
    fetchPolicy: 'network-only',
  }),
);

watchEffect(() => {
  const raw = registrationsResult.value?.getRegistrationsForCourseInCurrentCalendar ?? [];
  registrations.value = raw.map((r: Record<string, unknown>) => ({
    ...r,
    studentName: `${(r.student as Record<string, unknown>)?.surname || ''} ${(r.student as Record<string, unknown>)?.first_name || ''} ${(r.student as Record<string, unknown>)?.other_name || ''}`.trim(),
  }));
});

const goBack = () => router.back();
</script>
